import React, { useState, useRef, useMemo, useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useFela } from 'react-fela'
import { isEmpty, isEqual } from 'lodash'
import {
	__wrapper,
	__track,
	__baseTrack,
	__highlightedTrack,
	__sliderThumb,
	__sliderKnob,
	__valueIndicatorWrapper,
	__valueIndicator,
	__valueIndicatorText,
} from './__style'

function useAsyncReference(value) {
	const ref = useRef(value)
	const [, forceRender] = useState(false)

	function updateState(newState) {
		ref.current = newState
		forceRender(s => !s)
	}

	return [ref, updateState]
}

function RangeSlider({
	values = {},
	range = { min: 0, max: 2000 },
	onChange = () => {},
	unsetMargin = false,
	unsetMarginSide = false,
	step = 100,
	label = 'Price',
	disabled = false,
}) {
	const { css } = useFela({ unsetMargin, unsetMarginSide, disabled })

	const { min: minRange, max: maxRange } = range
	const rangeDiff = useMemo(() => maxRange - minRange, [maxRange, minRange])
	const [minDiff, setMinDiff] = useState(0)

	const [sliderWidth, setSliderWidth] = useState(0)
	const [offsetSliderWidth, setOffsetSliderWidth] = useState(0)
	const [currentMin, setCurrentMin] = useAsyncReference(
		values?.min || minRange
	)
	const [currentMax, setCurrentMax] = useAsyncReference(
		values?.max || maxRange
	)
	const [prevValue, setPrevValue] = useState(values)

	const inactiveSlider = useRef()

	const activeSlider = useRef()

	const [minThumbPos, setMinThumbPos] = useState(0)
	const [maxThumbPos, setMaxThumbPos] = useState(0)
	const [oldMin, setOldMin] = useState(currentMin.current)
	const [oldMax, setOldMax] = useState(currentMax.current)

	const updateCurrentMin = newMin => {
		if (newMin >= minRange && newMin <= currentMax.current - minDiff) {
			setCurrentMin(newMin)
		}
	}

	const updateCurrentMax = newMax => {
		if (newMax >= currentMin.current + minDiff && newMax <= maxRange) {
			setCurrentMax(newMax)
		}
	}

	const fixMinValue = () => {
		const diff = currentMin.current - oldMin
		const nearestValue = Math.round(diff / step) * step
		const currentMinValue = Math.min(oldMin + nearestValue, maxRange)

		if (
			currentMinValue >= minRange &&
			currentMinValue <= currentMax.current - minDiff
		) {
			updateCurrentMin(currentMinValue)
			setOldMin(currentMinValue)
		} else if (
			currentMinValue - step >= minRange &&
			currentMinValue - step <= currentMax.current - minDiff
		) {
			updateCurrentMin(currentMinValue - step)
			setOldMin(currentMinValue - step)
		} else {
			updateCurrentMin(oldMin)
		}
	}

	const fixMaxValue = () => {
		const diff = currentMax.current - oldMax
		const nearestValue = Math.round(diff / step) * step
		const currentMaxValue = Math.min(oldMax + nearestValue, maxRange)

		if (
			currentMaxValue >= currentMin.current + minDiff &&
			currentMaxValue <= maxRange
		) {
			updateCurrentMax(currentMaxValue)
			setOldMax(currentMaxValue)
		} else if (
			currentMaxValue + step >= currentMin.current + minDiff &&
			currentMaxValue + step <= maxRange
		) {
			updateCurrentMax(currentMaxValue + step)
			setOldMax(currentMaxValue + step)
		} else {
			updateCurrentMax(oldMax)
		}
	}

	const handleOnChange = () => {
		const currentValue = {
			min: currentMin.current,
			max: currentMax.current,
		}
		if (typeof onChange === 'function' && !isEqual(values, currentValue)) {
			onChange(currentValue)
		}
	}

	if (
		!isEmpty(values) &&
		!isEqual(values, prevValue) &&
		values.min < values.max
	) {
		const newValues = {
			min: currentMin.current,
			max: currentMax.current,
		}
		if (!isEqual(values, newValues)) {
			if (values?.min >= minRange && values?.min !== currentMin.current) {
				newValues.min = values.min
			}
			if (values?.max <= maxRange && values?.max !== currentMax.current) {
				newValues.max = values.max
			}

			setCurrentMin(newValues.min)
			setCurrentMax(newValues.max)
			setPrevValue(newValues)
		} else {
			setPrevValue(values)
		}
	} else if (!isEmpty(values) && values.min > values.max) {
		handleOnChange()
	}

	useLayoutEffect(() => {
		if (inactiveSlider.current) {
			const {
				width,
				left,
			} = inactiveSlider.current.getBoundingClientRect()

			const minMaxDiff = Math.max(
				Math.round((22 * rangeDiff) / width / 100) * 100,
				100
			)

			setSliderWidth(width)
			setOffsetSliderWidth(left)
			setMinDiff(minMaxDiff)
		}
	}, [inactiveSlider.current])

	useLayoutEffect(() => {
		if (activeSlider.current) {
			const leftPos =
				((currentMin.current - minRange) / rangeDiff) * sliderWidth
			const activeSliderWidthRatio =
				(currentMax.current - currentMin.current) / rangeDiff
			const rightPos = leftPos + activeSliderWidthRatio * sliderWidth

			activeSlider.current.style.transform = `translateX(${leftPos}px) scaleX(${activeSliderWidthRatio})`

			setMinThumbPos(leftPos)
			setMaxThumbPos(rightPos)
		}
	}, [activeSlider.current, currentMin.current, currentMax.current])

	const onMouseMoveMin = e => {
		const event = e.type.startsWith('touch') ? e.touches[0] : e

		const draggedWidth = event.clientX - offsetSliderWidth
		const draggedWidthInPercentage = (draggedWidth * 100) / sliderWidth
		const change = parseInt(
			(rangeDiff * draggedWidthInPercentage) / 100,
			10
		)
		const currentMinValue = Math.max(minRange + change, minRange)

		updateCurrentMin(currentMinValue)
	}

	const onMouseUpMin = () => {
		fixMinValue()
		handleOnChange()

		document.removeEventListener('mouseup', onMouseUpMin)
		document.removeEventListener('mousemove', onMouseMoveMin)

		document.removeEventListener('touchend', onMouseUpMin)
		document.removeEventListener('touchmove', onMouseMoveMin)
	}

	const changeMinValue = e => {
		e.preventDefault()
		setOldMin(currentMin.current)

		document.addEventListener('mousemove', onMouseMoveMin)
		document.addEventListener('mouseup', onMouseUpMin)

		document.addEventListener('touchmove', onMouseMoveMin)
		document.addEventListener('touchend', onMouseUpMin)
	}

	const onMouseMoveMax = e => {
		const event = e.type.startsWith('touch') ? e.touches[0] : e

		const draggedWidth = event.clientX - offsetSliderWidth
		const draggedWidthInPercentage = (draggedWidth * 100) / sliderWidth
		const change = parseInt(
			(rangeDiff * draggedWidthInPercentage) / 100,
			10
		)
		const currentMaxValue = Math.min(minRange + change, maxRange)

		updateCurrentMax(currentMaxValue)
	}

	const onMouseUpMax = () => {
		fixMaxValue()
		handleOnChange()

		document.removeEventListener('mouseup', onMouseUpMax)
		document.removeEventListener('mousemove', onMouseMoveMax)

		document.removeEventListener('touchend', onMouseUpMax)
		document.removeEventListener('touchmove', onMouseMoveMax)
	}

	const changeMaxValue = e => {
		e.preventDefault()

		document.addEventListener('mousemove', onMouseMoveMax)
		document.addEventListener('mouseup', onMouseUpMax)

		document.addEventListener('touchmove', onMouseMoveMax)
		document.addEventListener('touchend', onMouseUpMax)
	}

	const handleOnKeyDown = e => {
		if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
			if (e.target.dataset.name === 'minValueHandle') {
				updateCurrentMin(currentMin.current - step)
			} else if (e.target.dataset.name === 'maxValueHandle') {
				updateCurrentMax(currentMax.current - step)
			}
		}

		if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
			if (e.target.dataset.name === 'minValueHandle') {
				updateCurrentMin(currentMin.current + step)
			} else if (e.target.dataset.name === 'maxValueHandle') {
				updateCurrentMax(currentMax.current + step)
			}
		}
	}

	const handleTrackClick = e => {
		if (disabled) {
			return
		}
		const clickedPos = e.clientX - offsetSliderWidth
		const widthFromMinInPercentage = (clickedPos * 100) / sliderWidth
		const currentValue = parseInt(
			(rangeDiff * widthFromMinInPercentage) / 100,
			10
		)
		const diff =
			Math.abs(currentValue - currentMin.current) -
			Math.abs(currentMax.current - currentValue)

		if (diff < 0) {
			updateCurrentMin(currentValue)
			setTimeout(fixMinValue, 200)
		} else if (diff > 0) {
			updateCurrentMax(currentValue)
			setTimeout(fixMaxValue, 200)
		}
	}

	return (
		<div className={css(__wrapper)}>
			<div
				className={css(__track)}
				onClick={handleTrackClick}
				role="presentation"
			>
				<div className={css(__baseTrack)} ref={inactiveSlider} />
				<div className={css(__highlightedTrack)} ref={activeSlider} />
			</div>

			<div
				role="button"
				tabIndex={0}
				className={css(__sliderThumb)}
				data-name="minValueHandle"
				onMouseDown={changeMinValue}
				onTouchStart={changeMinValue}
				onKeyDown={handleOnKeyDown}
				disabled={disabled}
				style={{ transform: `translateX(${minThumbPos}px)` }}
				aria-label={`Minimum ${label} Range Indicator`}
			>
				<div className={`knob ${css(__sliderKnob)}`} />
				<div
					className={css(__valueIndicatorWrapper)}
					aria-hidden="true"
				>
					<div className={`indicator ${css(__valueIndicator)}`}>
						<span className={css(__valueIndicatorText)}>
							{currentMin.current}
						</span>
					</div>
				</div>
			</div>

			<div
				role="button"
				tabIndex={0}
				className={css(__sliderThumb)}
				data-name="maxValueHandle"
				onMouseDown={changeMaxValue}
				onTouchStart={changeMaxValue}
				onKeyDown={handleOnKeyDown}
				disabled={disabled}
				style={{ transform: `translateX(${maxThumbPos}px)` }}
				aria-label={`Maximum ${label} Range Indicator`}
			>
				<div className={`knob ${css(__sliderKnob)}`} />
				<div
					className={css(__valueIndicatorWrapper)}
					aria-hidden="true"
				>
					<div className={`indicator ${css(__valueIndicator)}`}>
						<span className={css(__valueIndicatorText)}>
							{currentMax.current}
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

RangeSlider.propTypes = {
	/** Min and Max Selected Values of the slider  */
	values: PropTypes.shape({
		min: PropTypes.number,
		max: PropTypes.number,
	}),
	/** Min and Max Range of the slider */
	range: PropTypes.shape({
		min: PropTypes.number,
		max: PropTypes.number,
	}),
	/** Callback to call when slider values are changed. */
	onChange: PropTypes.func,
	/** Enable steps in the Range Slider */
	step: PropTypes.number,
	/** Add label for accessibility to Range Slider */
	label: PropTypes.string,
	/** Set this to true to remove all margin from the Range Slider */
	unsetMargin: PropTypes.bool,
	/** Set this to true to remove side margin from the Range Slider */
	unsetMarginSide: PropTypes.bool,
	/** Set this to true disable the Range Slider */
	disabled: PropTypes.bool,
}

export default RangeSlider
